import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PageTitle from "../../layouts/PageTitle";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";
import {
  bulkEditPreviewFunc,
  clearBulkEditPreview,
} from "../../../store/custom-actions/products-actions/BulkEditPreviewSlicer";
import {
  productQuickUpdateFunc,
  clearQuickUpdateError,
} from "../../../store/custom-actions/products-actions/ProductQuickUpdateSlicer";

const DISCOUNT_TYPES = [
  { value: "", labelKey: "None" },
  { value: "fixed", labelKey: "Fixed" },
  { value: "percentage", labelKey: "Percentage" },
];

/** Fields that can be synced between product and variants (variants have no min_stock) */
const SYNCABLE_FIELDS = [
  { key: "price", labelKey: "Price" },
  { key: "discount", labelKey: "Discount" },
  { key: "discount_type", labelKey: "Discount Type" },
  { key: "stock", labelKey: "Stock" },
];

function toNum(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function toInt(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

function productToFormState(p) {
  return {
    price: p.price ?? "",
    discount: p.discount ?? "",
    discount_type: p.discount_type ?? "",
    stock: p.stock ?? "",
    min_stock: p.min_stock ?? "",
    variants: (p.variants || []).map((v) => ({
      id: v.id,
      sku: v.sku,
      image_url: v.image_url ?? null,
      price: v.price ?? "",
      discount: v.discount ?? "",
      discount_type: v.discount_type ?? "",
      stock: v.stock ?? "",
    })),
  };
}

function formStateToPayload(baseProduct, form) {
  const payload = {};
  if (toNum(form.price) !== toNum(baseProduct.price)) payload.price = toNum(form.price);
  if (toNum(form.discount) !== toNum(baseProduct.discount)) payload.discount = toNum(form.discount);
  if (form.discount_type !== (baseProduct.discount_type ?? "")) payload.discount_type = form.discount_type || null;
  if (toInt(form.stock) !== toInt(baseProduct.stock)) payload.stock = toInt(form.stock);
  if (toInt(form.min_stock) !== toInt(baseProduct.min_stock)) payload.min_stock = toInt(form.min_stock);

  const baseVariants = baseProduct.variants || [];
  const changedVariants = [];
  form.variants.forEach((fv) => {
    const bv = baseVariants.find((x) => x.id === fv.id);
    if (!bv) return;
    const vPayload = { id: fv.id };
    let hasChange = false;
    if (toNum(fv.price) !== toNum(bv.price)) {
      vPayload.price = toNum(fv.price);
      hasChange = true;
    }
    if (toNum(fv.discount) !== toNum(bv.discount)) {
      vPayload.discount = toNum(fv.discount);
      hasChange = true;
    }
    if ((fv.discount_type ?? "") !== (bv.discount_type ?? "")) {
      vPayload.discount_type = fv.discount_type || null;
      hasChange = true;
    }
    if (toInt(fv.stock) !== toInt(bv.stock)) {
      vPayload.stock = toInt(fv.stock);
      hasChange = true;
    }
    if (hasChange) changedVariants.push(vPayload);
  });
  if (changedVariants.length) payload.variants = changedVariants;
  return payload;
}

function hasFormChanges(baseProduct, form) {
  if (toNum(form.price) !== toNum(baseProduct.price)) return true;
  if (toNum(form.discount) !== toNum(baseProduct.discount)) return true;
  if ((form.discount_type ?? "") !== (baseProduct.discount_type ?? "")) return true;
  if (toInt(form.stock) !== toInt(baseProduct.stock)) return true;
  if (toInt(form.min_stock) !== toInt(baseProduct.min_stock)) return true;
  const baseVariants = baseProduct.variants || [];
  for (const fv of form.variants) {
    const bv = baseVariants.find((x) => x.id === fv.id);
    if (!bv) continue;
    if (toNum(fv.price) !== toNum(bv.price)) return true;
    if (toNum(fv.discount) !== toNum(bv.discount)) return true;
    if ((fv.discount_type ?? "") !== (bv.discount_type ?? "")) return true;
    if (toInt(fv.stock) !== toInt(bv.stock)) return true;
  }
  return false;
}

function ProductCard({ product, formState, setFormState, onSave, saving, error, t }) {
  const base = product;
  const form = formState;
  const setForm = setFormState;
  const hasChanges = hasFormChanges(base, form);
  const [syncFields, setSyncFields] = useState(
    SYNCABLE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: false }), {})
  );

  const handleProductChange = useCallback(
    (field, value) => setForm((prev) => ({ ...prev, [field]: value })),
    [setForm]
  );
  const handleVariantChange = useCallback(
    (variantId, field, value) => {
      setForm((prev) => ({
        ...prev,
        variants: prev.variants.map((v) =>
          v.id === variantId ? { ...v, [field]: value } : v
        ),
      }));
    },
    [setForm]
  );

  const toggleSyncField = useCallback((key) => {
    setSyncFields((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const selectAllSyncFields = useCallback(() => {
    setSyncFields(SYNCABLE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: true }), {}));
  }, []);
  const deselectAllSyncFields = useCallback(() => {
    setSyncFields(SYNCABLE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: false }), {}));
  }, []);
  const allSyncSelected = SYNCABLE_FIELDS.every((f) => syncFields[f.key]);

  const hasAnySyncSelected = SYNCABLE_FIELDS.some((f) => syncFields[f.key]);
  const syncProductToVariants = useCallback(() => {
    if (!hasAnySyncSelected || form.variants.length === 0) return;
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((v) => {
        const next = { ...v };
        SYNCABLE_FIELDS.forEach(({ key }) => {
          if (syncFields[key]) next[key] = prev[key];
        });
        return next;
      }),
    }));
  }, [setForm, syncFields, hasAnySyncSelected, form.variants.length]);

  const syncFirstVariantToProduct = useCallback(() => {
    if (!hasAnySyncSelected || form.variants.length === 0) return;
    const first = form.variants[0];
    setForm((prev) => {
      const next = { ...prev };
      SYNCABLE_FIELDS.forEach(({ key }) => {
        if (syncFields[key]) next[key] = first[key];
      });
      return next;
    });
  }, [setForm, syncFields, hasAnySyncSelected, form.variants.length]);

  return (
    <div className="card border shadow-sm mb-3 bulk-edit-product-card">
      <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2 py-3">
        <div className="d-flex align-items-center flex-wrap gap-2">
          {base.thumbnail_url ? (
            <img
              src={base.thumbnail_url}
              alt=""
              className="rounded"
              style={{ width: 40, height: 40, objectFit: "cover" }}
            />
          ) : (
            <div
              className="rounded bg-secondary d-flex align-items-center justify-content-center text-white"
              style={{ width: 40, height: 40 }}
            >
              <i className="fas fa-image" style={{ fontSize: "1rem" }} />
            </div>
          )}
          <div>
            <div className="fw-semibold text-dark">
              {base.title || t("Untitled")}
            </div>
            <small className="text-muted">
              <span className="badge bg-secondary me-1">SKU</span>
              {base.sku}
            </small>
          </div>
          {hasChanges && (
            <span className="badge bg-warning text-dark">{t("Unsaved changes")}</span>
          )}
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!hasChanges || saving}
          onClick={() => onSave(base, form)}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              {t("Saving...")}
            </>
          ) : (
            <>
              <i className="fas fa-save me-1" />
              {t("Update")}
            </>
          )}
        </button>
      </div>
      {error && (
        <div className="alert alert-danger mb-0 rounded-0">
          {typeof error === "string" ? error : error?.message || t("Update failed")}
        </div>
      )}
      <div className="card-body">
        <div className="row g-3 mb-3">
          <div className="col-6 col-md">
            <label className="form-label small text-muted mb-1">{t("Price")}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="form-control form-control-sm"
              value={form.price}
              onChange={(e) => handleProductChange("price", e.target.value)}
            />
          </div>
          <div className="col-6 col-md">
            <label className="form-label small text-muted mb-1">{t("Discount")}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="form-control form-control-sm"
              value={form.discount}
              onChange={(e) => handleProductChange("discount", e.target.value)}
            />
          </div>
          <div className="col-6 col-md">
            <label className="form-label small text-muted mb-1">{t("Discount Type")}</label>
            <select
              className="form-select form-select-sm"
              value={form.discount_type}
              onChange={(e) => handleProductChange("discount_type", e.target.value)}
            >
              {DISCOUNT_TYPES.map((opt) => (
                <option key={opt.value || "none"} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md">
            <label className="form-label small text-muted mb-1">{t("Stock")}</label>
            <input
              type="number"
              min="0"
              className="form-control form-control-sm"
              value={form.stock}
              onChange={(e) => handleProductChange("stock", e.target.value)}
            />
          </div>
          <div className="col-6 col-md">
            <label className="form-label small text-muted mb-1">{t("Min. Stock")}</label>
            <input
              type="number"
              min="0"
              className="form-control form-control-sm"
              value={form.min_stock}
              onChange={(e) => handleProductChange("min_stock", e.target.value)}
            />
          </div>
        </div>

        {form.variants.length > 0 && (
          <div className="sync-section mt-3 mb-3 p-3 bg-white rounded border">
            <h6 className="text-muted mb-2">
              <i className="fas fa-sync-alt me-1" />
              {t("Sync with variants")}
            </h6>
            <p className="small text-muted mb-2">
              {t("Select fields to copy, then choose direction.")}
              <span className="ms-2">
                {allSyncSelected ? (
                  <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={deselectAllSyncFields}>
                    {t("Deselect all")}
                  </button>
                ) : (
                  <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={selectAllSyncFields}>
                    {t("Select all")}
                  </button>
                )}
              </span>
            </p>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {SYNCABLE_FIELDS.map(({ key, labelKey }) => (
                  <label key={key} className="form-check form-check-inline mb-0 small">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!syncFields[key]}
                      onChange={() => toggleSyncField(key)}
                    />
                    <span className="form-check-label">{t(labelKey)}</span>
                  </label>
                ))}
              </div>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={!hasAnySyncSelected}
                  onClick={syncProductToVariants}
                  title={t("Copy product values to all variants")}
                >
                  <i className="fas fa-arrow-down me-1" />
                  {t("Product → Variants")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={!hasAnySyncSelected}
                  onClick={syncFirstVariantToProduct}
                  title={t("Copy first variant values to product")}
                >
                  <i className="fas fa-arrow-up me-1" />
                  {t("Variant → Product")}
                </button>
              </div>
            </div>
          </div>
        )}

        {form.variants.length > 0 && (
          <div className="variants-section mt-3 pt-3 border-top">
            <h6 className="text-muted mb-2">
              <i className="fas fa-layer-group me-1" />
              {t("Variants")} ({form.variants.length})
            </h6>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead className="table-white">
                  <tr>
                    <th style={{ width: 50 }}>{t("Image")}</th>
                    <th>{t("SKU")}</th>
                    <th>{t("Price")}</th>
                    <th>{t("Discount")}</th>
                    <th>{t("Type")}</th>
                    <th>{t("Stock")}</th>
                  </tr>
                </thead>
                <tbody>
                  {form.variants.map((v) => (
                    <tr key={v.id}>
                      <td className="align-middle text-center">
                        {v.image_url ? (
                          <img
                            src={v.image_url}
                            alt=""
                            className="rounded"
                            style={{ width: 32, height: 32, objectFit: "cover" }}
                          />
                        ) : (
                          <span className="text-muted">
                            <i className="fas fa-image" style={{ fontSize: "0.75rem" }} />
                          </span>
                        )}
                      </td>
                      <td className="align-middle">
                        <small className="text-muted">{v.sku}</small>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm"
                          value={v.price}
                          onChange={(e) => handleVariantChange(v.id, "price", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control form-control-sm"
                          value={v.discount}
                          onChange={(e) => handleVariantChange(v.id, "discount", e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={v.discount_type}
                          onChange={(e) => handleVariantChange(v.id, "discount_type", e.target.value)}
                        >
                          {DISCOUNT_TYPES.map((opt) => (
                            <option key={opt.value || "none"} value={opt.value}>
                              {t(opt.labelKey)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          value={v.stock}
                          onChange={(e) => handleVariantChange(v.id, "stock", e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BulkEditProductsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const idsFromUrl = searchParams.get("ids");
  const ids = useMemo(() => {
    if (!idsFromUrl) return [];
    return idsFromUrl
      .split(",")
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !Number.isNaN(id));
  }, [idsFromUrl]);

  const { data: products, loading: previewLoading, error: previewError } = useSelector(
    (state) => state.bulkEditPreviewSlicer
  );
  const { loadingIds, errorByProduct } = useSelector(
    (state) => state.productQuickUpdateSlicer
  );

  const [productsState, setProductsState] = useState([]);
  const [formStates, setFormStates] = useState({});

  useEffect(() => {
    if (ids.length === 0) return;
    dispatch(clearBulkEditPreview());
    dispatch(bulkEditPreviewFunc(ids));
  }, [ids.join(","), dispatch]);

  useEffect(() => {
    if (!products || !products.length) {
      setProductsState([]);
      setFormStates({});
      return;
    }
    setProductsState(products);
    const next = {};
    products.forEach((p) => {
      next[p.id] = productToFormState(p);
    });
    setFormStates(next);
  }, [products]);

  const setFormForProduct = useCallback((productId, updater) => {
    setFormStates((prev) => ({
      ...prev,
      [productId]: typeof updater === "function" ? updater(prev[productId]) : updater,
    }));
  }, []);

  const handleSave = useCallback(
    async (baseProduct, form) => {
      const payload = formStateToPayload(baseProduct, form);
      if (Object.keys(payload).length === 0) return;
      dispatch(clearQuickUpdateError(baseProduct.id));
      const result = await dispatch(
        productQuickUpdateFunc({ productId: baseProduct.id, data: payload })
      );
      if (productQuickUpdateFunc.fulfilled.match(result)) {
        const updated = result.payload.product;
        const merged = {
          ...updated,
          title: updated.title ?? baseProduct.title,
          thumbnail_url: updated.thumbnail_url ?? baseProduct.thumbnail_url,
          variants: (updated.variants || []).map((uv) => {
            const prevV = (baseProduct.variants || []).find((v) => v.id === uv.id);
            return {
              ...uv,
              sku: uv.sku ?? prevV?.sku,
              image_url: uv.image_url ?? prevV?.image_url ?? null,
            };
          }),
        };
        setProductsState((prev) =>
          prev.map((p) => (p.id === merged.id ? merged : p))
        );
        setFormStates((prev) => ({
          ...prev,
          [merged.id]: productToFormState(merged),
        }));
      }
    },
    [dispatch]
  );

  if (ids.length === 0) {
    return (
      <>
        <PageTitle motherMenu="Dashboard" activeMenu="Products" motherMenuPath="/products" />
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="fas fa-box-open fa-3x text-muted mb-3" />
              <h5>{t("No products selected")}</h5>
              <p className="text-muted mb-3">
                {t("Select products from the products list and use Bulk Actions → Update prices & quantities.")}
              </p>
              <Link to="/products" className="btn btn-primary">
                <i className="fas fa-arrow-left me-2" />
                {t("Back to Products")}
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle motherMenu="Products" activeMenu="Bulk Edit Prices & Quantities" motherMenuPath="/products" />
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <Link to="/products" className="btn btn-outline-secondary btn-sm">
            <i className="fas fa-arrow-left me-1" />
            {t("Back to Products")}
          </Link>
          <span className="text-muted">
            {t("Editing")} <strong>{ids.length}</strong> {t("products")}
          </span>
        </div>

        {previewLoading && (
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("Loading")}...</span>
              </div>
              <p className="mt-2 mb-0 text-muted">{t("Loading products...")}</p>
            </div>
          </div>
        )}

        {!previewLoading && previewError && (
          <div className="alert alert-danger">
            {typeof previewError === "string"
              ? previewError
              : previewError?.message || t("Failed to load products")}
            <Link to="/products" className="alert-link ms-2">
              {t("Back to Products")}
            </Link>
          </div>
        )}

        {!previewLoading && !previewError && productsState.length === 0 && (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="fas fa-search fa-3x text-muted mb-3" />
              <h5>{t("No products found")}</h5>
              <Link to="/products" className="btn btn-primary mt-2">
                {t("Back to Products")}
              </Link>
            </div>
          </div>
        )}

        {!previewLoading && !previewError && productsState.length > 0 && (
          <div className="bulk-edit-products-list">
            {productsState.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                formState={formStates[product.id] || productToFormState(product)}
                setFormState={(updater) =>
                  setFormForProduct(product.id, updater)
                }
                onSave={handleSave}
                saving={loadingIds[product.id]}
                error={errorByProduct[product.id]}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
      <LoadingScreen loading={false} />
    </>
  );
}
